/**
 * Visite guidée produit (driver.js) — Phase 1.
 * Présente les modules de la plateforme. Robuste : les étapes ancrées à un
 * élément absent/masqué (mobile, etc.) sont retirées → jamais de blocage.
 * « Passer la démo » = bouton de fermeture (X) natif. État mémorisé.
 */
import { driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";

const SEEN_KEY = "victor.tour.done.v1";

export function hasSeenTour(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

function markSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, "1");
  } catch {
    /* stockage indisponible */
  }
}

function isVisible(selector: string): boolean {
  const el = document.querySelector<HTMLElement>(selector);
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

export function startProductTour(): void {
  const allSteps: DriveStep[] = [
    {
      popover: {
        title: "Bienvenue sur MyVictor 👋",
        description:
          "Une visite rapide de votre espace, en moins d'une minute. Vous pouvez la passer à tout moment.",
      },
    },
    {
      element: '[data-tour="sidebar"]',
      popover: {
        title: "Tous vos modules",
        description:
          "Le menu de gauche réunit vos outils : planning, pointages, agents, conformité (CNAPS / SSIAP / CCN), facturation et pilotage.",
        side: "right",
        align: "center",
      },
    },
    {
      element: '[data-tour="topbar"]',
      popover: {
        title: "Recherche & accès rapide",
        description:
          "Retrouvez tout en un instant et ouvrez la palette de commandes avec Cmd/Ctrl + K.",
        side: "bottom",
        align: "end",
      },
    },
    {
      popover: {
        title: "Le planning intelligent",
        description:
          "Construisez vos vacations en glisser-déposer. Victor connaît la convention collective et bloque les affectations non conformes.",
      },
    },
    {
      popover: {
        title: "L'app agent",
        description:
          "Vos agents pointent, font leurs rondes et leur main courante depuis leur mobile, même hors connexion.",
      },
    },
    {
      popover: {
        title: "Conformité automatique",
        description:
          "Cartes professionnelles, SSIAP et recyclages suivis en continu, avec alertes avant expiration.",
      },
    },
    {
      popover: {
        title: "Facturation & pilotage",
        description:
          "Facturez vos heures réelles en un clic (prêt e-invoicing 2026) et suivez votre marge par site, client et agent.",
      },
    },
    {
      popover: {
        title: "C'est à vous 🚀",
        description:
          "Vous pourrez relancer cette visite à tout moment via le bouton « ? » en haut à droite.",
      },
    },
  ];

  // Garde : on enlève les étapes dont l'élément n'est pas visible (mobile…).
  const steps = allSteps.filter((s) => !s.element || isVisible(s.element as string));

  const d = driver({
    showProgress: true,
    popoverClass: "victor-tour",
    nextBtnText: "Suivant",
    prevBtnText: "Précédent",
    doneBtnText: "Terminer",
    progressText: "{{current}} / {{total}}",
    overlayColor: "#071A33",
    overlayOpacity: 0.6,
    stagePadding: 6,
    stageRadius: 14,
    steps,
    onDestroyed: () => markSeen(),
  });
  d.drive();
}
